import React, { useState, useCallback, useEffect } from 'react';
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import PageHeader from './components/PageHeader';
import { formatPrice } from './utils/formatPrice';
import client from './api/client';
import './ReportsPage.css';

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const ISK_ABBREV_THRESHOLD = 1_000_000;

function fmtIsk(value) {
  if (value === null || value === undefined) return '—';
  if (value >= 1_000_000_000_000) return `${(value / 1_000_000_000_000).toFixed(2)}T ISK`;
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B ISK`;
  if (value >= ISK_ABBREV_THRESHOLD) return `${(value / 1_000_000).toFixed(2)}M ISK`;
  return formatPrice(value);
}

function fmtNum(n) {
  return new Intl.NumberFormat().format(n);
}

function SummaryCard({ label, value, className }) {
  return (
    <div className={`summary-card ${className || ''}`}>
      <div className="card-label">{label}</div>
      <div className="card-value">{value}</div>
    </div>
  );
}

function RankTable({ title, rows, columns }) {
  return (
    <div className="report-table-section">
      <h2>{title}</h2>
      {rows.length === 0 ? (
        <div className="reports-empty">No data</div>
      ) : (
        <table className="data-table report-table">
          <thead>
            <tr>
              <th>#</th>
              {columns.map((col) => (
                <th key={col.key} className={col.align === 'right' ? 'right' : ''}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td className="rank-num">{i + 1}</td>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={[col.align === 'right' ? 'right' : '', col.className || ''].join(' ')}
                    title={col.raw ? String(row[col.raw]) : undefined}
                  >
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PvcBar({ label, sellIsk, buyIsk, maxIsk }) {
  const sellPct = maxIsk ? (sellIsk / maxIsk) * 100 : 0;
  const buyPct = maxIsk ? (buyIsk / maxIsk) * 100 : 0;
  return (
    <div className="pvc-grid">
      <div className="pvc-row">
        <div className="pvc-label">{label} — Sold</div>
        <div className="pvc-bar-container">
          <div className="pvc-bar-track">
            <div className="pvc-bar-fill sell" style={{ width: `${sellPct}%` }} />
          </div>
          <span className="pvc-bar-value isk-sell">{fmtIsk(sellIsk)}</span>
        </div>
      </div>
      <div className="pvc-row">
        <div className="pvc-label">{label} — Bought</div>
        <div className="pvc-bar-container">
          <div className="pvc-bar-track">
            <div className="pvc-bar-fill buy" style={{ width: `${buyPct}%` }} />
          </div>
          <span className="pvc-bar-value isk-buy">{fmtIsk(buyIsk)}</span>
        </div>
      </div>
    </div>
  );
}

const PRESETS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: 'All', days: null },
];

export default function ReportsPage() {
  const [start, setStart] = useState(daysAgo(30));
  const [end, setEnd] = useState(today());
  const [includePersonal, setIncludePersonal] = useState(true);
  const [includeCorp, setIncludeCorp] = useState(true);
  const [activePreset, setActivePreset] = useState('30d');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartMode, setChartMode] = useState('period'); // 'period' | 'cumulative'

  const fetchReport = useCallback(async (s, e, personal, corp) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ start: s, end: e });
      if (!personal) params.append('personal', 'false');
      if (!corp) params.append('corp', 'false');
      const resp = await client.get(`/reports/market/?${params}`);
      setData(resp.data);
    } catch (err) {
      setError('Failed to load report data. Make sure your wallet transactions are refreshed.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(start, end, includePersonal, includeCorp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePreset = (preset) => {
    setActivePreset(preset.label);
    const newEnd = today();
    const newStart = preset.days ? daysAgo(preset.days) : '2000-01-01';
    setStart(newStart);
    setEnd(newEnd);
    fetchReport(newStart, newEnd, includePersonal, includeCorp);
  };

  const handleApply = () => {
    setActivePreset(null);
    fetchReport(start, end, includePersonal, includeCorp);
  };

  const pvc = data?.personal_vs_corp;
  const maxPvc = pvc
    ? Math.max(pvc.personal_sell, pvc.personal_buy, pvc.corp_sell, pvc.corp_buy, 1)
    : 1;

  const stationCols = [
    { key: 'name', label: 'Station', render: (r) => r.name },
    { key: 'sell_isk', label: 'Sold', align: 'right', className: 'isk-sell', render: (r) => fmtIsk(r.sell_isk) },
    { key: 'buy_isk', label: 'Bought', align: 'right', className: 'isk-buy', render: (r) => fmtIsk(r.buy_isk) },
    { key: 'total_isk', label: 'Total', align: 'right', className: 'isk-total', render: (r) => fmtIsk(r.total_isk) },
    { key: 'transaction_count', label: 'Txns', align: 'right', className: 'tx-count', render: (r) => fmtNum(r.transaction_count) },
  ];

  const counterpartyCols = [
    { key: 'name', label: 'Character / Corp', render: (r) => r.name },
    { key: 'isk_volume', label: 'ISK Volume', align: 'right', className: 'isk-sell', render: (r) => fmtIsk(r.isk_volume) },
    { key: 'transaction_count', label: 'Txns', align: 'right', className: 'tx-count', render: (r) => fmtNum(r.transaction_count) },
  ];

  const buyCounterpartyCols = counterpartyCols.map((c) =>
    c.key === 'isk_volume' ? { ...c, className: 'isk-buy' } : c
  );

  const itemCols = (iskClass) => [
    { key: 'name', label: 'Item', render: (r) => r.name },
    { key: 'isk_volume', label: 'ISK Volume', align: 'right', className: iskClass, render: (r) => fmtIsk(r.isk_volume) },
    { key: 'quantity', label: 'Qty', align: 'right', className: 'tx-count', render: (r) => fmtNum(r.quantity) },
    { key: 'transaction_count', label: 'Txns', align: 'right', className: 'tx-count', render: (r) => fmtNum(r.transaction_count) },
  ];

  const charCols = [
    { key: 'name', label: 'Character', render: (r) => r.name },
    { key: 'sell_isk', label: 'Sold', align: 'right', className: 'isk-sell', render: (r) => fmtIsk(r.sell_isk) },
    { key: 'buy_isk', label: 'Bought', align: 'right', className: 'isk-buy', render: (r) => fmtIsk(r.buy_isk) },
    { key: 'transaction_count', label: 'Txns', align: 'right', className: 'tx-count', render: (r) => fmtNum(r.transaction_count) },
  ];

  const pnlCols = (profitClass) => [
    { key: 'name', label: 'Item', render: (r) => r.name },
    { key: 'profit', label: 'Realised P&L', align: 'right', className: profitClass, render: (r) => fmtIsk(Math.abs(r.profit)) },
    { key: 'avg_sell', label: 'Avg Sell', align: 'right', className: 'isk-sell', render: (r) => fmtIsk(r.avg_sell) },
    { key: 'avg_buy', label: 'Avg Buy', align: 'right', className: 'isk-buy', render: (r) => fmtIsk(r.avg_buy) },
    { key: 'sell_qty', label: 'Qty Sold', align: 'right', className: 'tx-count', render: (r) => fmtNum(r.sell_qty) },
  ];

  const chartTooltipFormatter = (value, name) => [fmtIsk(value), name];

  const cumulativeData = data
    ? data.volume_over_time.reduce((acc, row) => {
        const prev = acc[acc.length - 1] || { cum_sell: 0, cum_buy: 0 };
        acc.push({
          date: row.date,
          cum_sell: prev.cum_sell + row.sell_isk,
          cum_buy: prev.cum_buy + row.buy_isk,
        });
        return acc;
      }, [])
    : [];

  return (
    <div className="page reports-container">
      <PageHeader
        title="Market Reports"
        meta={data && [
          `${data.summary.transaction_count.toLocaleString()} transactions`,
          `${data.summary.start} → ${data.summary.end}`,
        ]}
      />

      {/* Controls */}
      <div className="reports-controls">
        <div className="control-group">
          <label>Preset</label>
          <div className="seg">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                className={`seg-btn ${activePreset === p.label ? 'active' : ''}`}
                onClick={() => handlePreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="control-group">
          <label>Start</label>
          <input type="date" value={start} onChange={(e) => { setStart(e.target.value); setActivePreset(null); }} />
        </div>
        <div className="control-group">
          <label>End</label>
          <input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setActivePreset(null); }} />
        </div>
        <div className="control-group">
          <label>Include</label>
          <div className="toggle-group">
            <label>
              <input type="checkbox" checked={includePersonal} onChange={(e) => setIncludePersonal(e.target.checked)} />
              Personal
            </label>
            <label>
              <input type="checkbox" checked={includeCorp} onChange={(e) => setIncludeCorp(e.target.checked)} />
              Corp
            </label>
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleApply}>Apply</button>
      </div>

      {loading && <div className="reports-loading">Loading report…</div>}
      {error && <div className="reports-empty">{error}</div>}

      {!loading && !error && data && (
        <>
          {/* Summary */}
          <div className="summary-cards">
            <SummaryCard label="Total ISK Sold" value={fmtIsk(data.summary.total_sell_isk)} className="sell" />
            <SummaryCard label="Total ISK Bought" value={fmtIsk(data.summary.total_buy_isk)} className="buy" />
            <SummaryCard
              label="Realised Net ISK"
              value={fmtIsk(data.summary.total_sell_isk - data.summary.total_buy_isk)}
              className={data.summary.total_sell_isk >= data.summary.total_buy_isk ? 'sell' : 'buy'}
            />
            <SummaryCard label="Transactions" value={fmtNum(data.summary.transaction_count)} />
          </div>

          {/* Volume over time chart */}
          <div className="chart-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ margin: 0 }}>
                ISK Volume Over Time&nbsp;
                <span style={{ fontSize: '0.8rem', color: 'var(--text-faint)', fontWeight: 'normal' }}>
                  ({data.summary.granularity})
                </span>
              </h2>
              <div className="seg">
                <button
                  className={`seg-btn ${chartMode === 'period' ? 'active' : ''}`}
                  onClick={() => setChartMode('period')}
                >
                  Per Period
                </button>
                <button
                  className={`seg-btn ${chartMode === 'cumulative' ? 'active' : ''}`}
                  onClick={() => setChartMode('cumulative')}
                >
                  Cumulative
                </button>
              </div>
            </div>
            {data.volume_over_time.length === 0 ? (
              <div className="reports-empty">No data for this period</div>
            ) : chartMode === 'period' ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.volume_over_time} margin={{ top: 4, right: 20, left: 20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--line-strong)" />
                  <XAxis dataKey="date" tick={{ fill: 'var(--text-dim)', fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => fmtIsk(v)} tick={{ fill: 'var(--text-dim)', fontSize: 11 }} width={90} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'var(--surface)', border: '1px solid var(--line-strong)', borderRadius: 6 }}
                    labelStyle={{ color: 'var(--text-dim)' }}
                    formatter={chartTooltipFormatter}
                  />
                  <Legend wrapperStyle={{ color: 'var(--text-dim)' }} />
                  <Bar dataKey="sell_isk" name="Sold" fill="var(--pos)" />
                  <Bar dataKey="buy_isk" name="Bought" fill="var(--warn)" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={cumulativeData} margin={{ top: 4, right: 20, left: 20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradSell" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--pos)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="var(--pos)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradBuy" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--warn)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="var(--warn)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--line-strong)" />
                  <XAxis dataKey="date" tick={{ fill: 'var(--text-dim)', fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => fmtIsk(v)} tick={{ fill: 'var(--text-dim)', fontSize: 11 }} width={90} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'var(--surface)', border: '1px solid var(--line-strong)', borderRadius: 6 }}
                    labelStyle={{ color: 'var(--text-dim)' }}
                    formatter={chartTooltipFormatter}
                  />
                  <Legend wrapperStyle={{ color: 'var(--text-dim)' }} />
                  <Area type="monotone" dataKey="cum_sell" name="Sold" stroke="var(--pos)" fill="url(#gradSell)" strokeWidth={2} />
                  <Area type="monotone" dataKey="cum_buy" name="Bought" stroke="var(--warn)" fill="url(#gradBuy)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Hourly sell pattern */}
          {data.hourly_sells && (
            <div className="chart-section">
              <h2>
                Average Sell Volume by Hour&nbsp;
                <span style={{ fontSize: '0.8rem', color: 'var(--text-faint)', fontWeight: 'normal' }}>
                  (daily avg over period · UTC)
                </span>
              </h2>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.hourly_sells} margin={{ top: 4, right: 20, left: 20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--line-strong)" />
                  <XAxis dataKey="label" tick={{ fill: 'var(--text-dim)', fontSize: 10 }} interval={0} />
                  <YAxis tickFormatter={(v) => fmtIsk(v)} tick={{ fill: 'var(--text-dim)', fontSize: 11 }} width={90} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'var(--surface)', border: '1px solid var(--line-strong)', borderRadius: 6 }}
                    labelStyle={{ color: 'var(--text-dim)' }}
                    formatter={(v, name) => [fmtIsk(v), 'Avg ISK Sold']}
                  />
                  <Bar dataKey="avg_isk" name="Avg ISK Sold" fill="var(--pos)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Personal vs Corp */}
          {pvc && (
            <div className="pvc-section">
              <h2>Personal vs Corporation</h2>
              <PvcBar label="Personal" sellIsk={pvc.personal_sell} buyIsk={pvc.personal_buy} maxIsk={maxPvc} />
              <div style={{ marginTop: 12 }}>
                <PvcBar label="Corp" sellIsk={pvc.corp_sell} buyIsk={pvc.corp_buy} maxIsk={maxPvc} />
              </div>
            </div>
          )}

          {/* P&L tables */}
          {(data.top_profits.length > 0 || data.top_losses.length > 0) && (
            <>
              <div style={{ color: 'var(--text-faint)', fontSize: '0.8rem', marginBottom: 8 }}>
                Items with both a buy and a sell in the window. Realised P&amp;L = (avg sell − avg buy) × units sold — unit margin avoids distortion from bulk seeding purchases.
              </div>
              <div className="tables-grid" style={{ marginBottom: 24 }}>
                <RankTable title="Top Profits" rows={data.top_profits} columns={pnlCols('isk-sell')} />
                <RankTable title="Top Losses" rows={data.top_losses} columns={pnlCols('isk-buy')} />
              </div>
            </>
          )}

          {/* Ranked tables */}
          <div className="tables-grid">
            <RankTable title="Top Stations by Volume" rows={data.top_stations} columns={stationCols} />
            <RankTable title="Top Sell-To Counterparties" rows={data.top_sell_to} columns={counterpartyCols} />
            <RankTable title="Top Buy-From Counterparties" rows={data.top_buy_from} columns={buyCounterpartyCols} />
            <RankTable title="Top Items Sold" rows={data.top_items_sold} columns={itemCols('isk-sell')} />
            <RankTable title="Top Items Bought" rows={data.top_items_bought} columns={itemCols('isk-buy')} />
            {data.by_character.length > 0 && (
              <RankTable title="By Character" rows={data.by_character} columns={charCols} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
